const OPENPAY_JS = 'https://js.openpay.mx/openpay.v1.min.js';
const OPENPAY_DATA_JS = 'https://js.openpay.mx/openpay-data.v1.min.js';

export interface OpenPayToken {
  id: string;
  card?: {
    brand?: string;
    card_number?: string;
  };
}

export interface OpenPayError {
  message?: string;
  description?: string;
  error_code?: number;
  category?: string;
}

export interface OpenPayCardParams {
  card_number: string;
  holder_name: string;
  expiration_year: string;
  expiration_month: string;
  cvv2: string;
}

export interface OpenPayStatic {
  setId: (id: string) => void;
  setApiKey: (key: string) => void;
  setSandboxMode: (flag: boolean) => void;
  token: {
    create: (
      params: OpenPayCardParams | { card: OpenPayCardParams },
      success: (response: { data: OpenPayToken }) => void,
      error: (response: { data: OpenPayError; message?: string }) => void,
    ) => void;
  };
  deviceData: {
    setup: (formId?: string, hiddenFieldName?: string) => string;
  };
  card: {
    validateCardNumber: (n: string) => boolean;
    validateCVC: (cvc: string, number?: string) => boolean;
    validateExpiry: (month: string, year: string) => boolean;
    cardType: (n: string) => string;
  };
}

declare global {
  interface Window {
    OpenPay?: OpenPayStatic;
  }
}

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = false;
  document.head.appendChild(script);
}

function waitForOpenPay(timeoutMs = 8000): Promise<OpenPayStatic> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const OpenPay = window.OpenPay;
      if (OpenPay) {
        resolve(OpenPay);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        reject(
          new Error(
            'OpenPay.js no cargó. Recargue con Ctrl+Shift+R o desactive el bloqueador.',
          ),
        );
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

export async function loadOpenPay(
  merchantId: string,
  publicKey: string,
  sandbox: boolean,
): Promise<OpenPayStatic> {
  if (!window.OpenPay) {
    injectScript(OPENPAY_JS);
    injectScript(OPENPAY_DATA_JS);
  }

  const OpenPay = await waitForOpenPay();
  OpenPay.setId(merchantId);
  OpenPay.setApiKey(publicKey);
  OpenPay.setSandboxMode(!!sandbox);
  return OpenPay;
}

export function fallbackDeviceSession(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 32; i += 1) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}

export function setupDeviceSession(OpenPay: OpenPayStatic): string {
  try {
    const id = OpenPay.deviceData?.setup?.();
    if (id) return String(id);
  } catch {
    /* sandbox a veces no entrega beacon de Sift */
  }
  return fallbackDeviceSession();
}

export function tokenizeCard(
  OpenPay: OpenPayStatic,
  card: OpenPayCardParams,
): Promise<string> {
  return new Promise((resolve, reject) => {
    OpenPay.token.create(
      card,
      (response) => {
        const id = response?.data?.id;
        if (!id) {
          reject(new Error('OpenPay no devolvió el token de la tarjeta'));
          return;
        }
        resolve(id);
      },
      (response) => {
        const msg =
          response?.data?.description ||
          response?.data?.message ||
          response?.message ||
          'No se pudo tokenizar la tarjeta';
        reject(new Error(msg));
      },
    );
  });
}

export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

export function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

export function browserHttpContext(): Record<string, string | boolean> {
  return {
    browser: navigator.userAgent,
    domain: window.location.hostname,
    browser_screen_height: String(window.screen.height),
    browser_screen_width: String(window.screen.width),
    browser_user_agent: navigator.userAgent,
    browser_language: navigator.language || 'es-MX',
    browser_java_enabled: false,
    browser_javascript_enabled: true,
  };
}
